import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useProgress } from '../../context/ProgressContext';
import { useAuth } from '../../context/useAuth';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [userDropdown, setUserDropdown] = useState(false);

  const { user, logout, isAuthenticated } = useAuth();
  const { enrollments = [] } = useProgress();

  const navigate = useNavigate();
  const location = useLocation();

  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setUserDropdown(false);
  }, [location.pathname]);

  const navLinks = [
    { label: 'Home', to: '/' },
    { label: 'Courses', to: '/courses' },
    ...(isAuthenticated ? [{ label: 'Dashboard', to: '/dashboard' }] : []),
  ];

  const handleLogout = () => {
    logout();
    navigate('/');
    setUserDropdown(false);
  };

  // Safe enrolled count
  const enrolledCount = user 
    ? enrollments.filter(e => e.userId === user.id).length 
    : 0;

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled || !isHome ? 'bg-white border-b border-slate-100' : 'bg-transparent'}`}>
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 whitespace-nowrap">
          <img src="https://public.readdy.ai/ai/img_res/a37d52a8-201a-4b92-9b36-08215345d1df.png" alt="LearnFlow" className="h-9 w-auto" />
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {navLinks.map(link => (
            <Link
              key={link.to}
              to={link.to}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                location.pathname === link.to
                  ? 'text-teal-600 bg-teal-50'
                  : scrolled || !isHome ? 'text-slate-600 hover:text-teal-600 hover:bg-slate-50' : 'text-white/90 hover:text-white hover:bg-white/10'
              }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {isAuthenticated && user ? (
            <div className="relative">
              <button
                onClick={() => setUserDropdown(!userDropdown)}
                className="flex items-center gap-2.5 cursor-pointer"
              >
                <img src={user.avatar} alt={user.name} className="w-8 h-8 rounded-full object-cover border-2 border-teal-200" />
                <div className="text-left">
                  <p className={`text-sm font-semibold leading-none ${scrolled || !isHome ? 'text-slate-800' : 'text-white'}`}>{user.name}</p>
                  <p className={`text-xs leading-none mt-0.5 ${scrolled || !isHome ? 'text-slate-400' : 'text-white/60'}`}>{enrolledCount} course{enrolledCount !== 1 ? 's' : ''}</p>
                </div>
                <div className={`w-4 h-4 flex items-center justify-center ${scrolled || !isHome ? 'text-slate-400' : 'text-white/60'}`}>
                  <i className={`ri-arrow-${userDropdown ? 'up' : 'down'}-s-line text-sm`} />
                </div>
              </button>
              {userDropdown && (
                <div className="absolute right-0 top-12 w-52 bg-white rounded-xl border border-slate-100 overflow-hidden">
                  <div className="p-3 border-b border-slate-50">
                    <p className="text-sm font-semibold text-slate-800">{user.name}</p>
                    <p className="text-xs text-slate-400 truncate">{user.email}</p>
                  </div>
                  {['admin', 'administrator', 'superadmin', 'instructor'].includes((user.role || '').toLowerCase()) ? (
                    <Link to="/admin" className="flex items-center gap-2 px-3 py-2.5 text-sm text-teal-600 hover:bg-teal-50 cursor-pointer font-semibold">
                      <div className="w-4 h-4 flex items-center justify-center"><i className="ri-dashboard-3-line text-teal-600" /></div>
                      {user.role === 'instructor' ? 'Instructor Dashboard' : 'Admin Command Center'}
                    </Link>
                  ) : (
                    <Link to="/dashboard" className="flex items-center gap-2 px-3 py-2.5 text-sm text-slate-600 hover:bg-slate-50 cursor-pointer">
                      <div className="w-4 h-4 flex items-center justify-center"><i className="ri-dashboard-line" /></div>
                      My Dashboard
                    </Link>
                  )}
                  {['admin', 'administrator', 'superadmin'].includes((user.role || '').toLowerCase()) && (
                    <Link to="/admin/courses" className="flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 cursor-pointer">
                      <div className="w-4 h-4 flex items-center justify-center"><i className="ri-book-open-line text-slate-400" /></div>
                      Manage Courses
                    </Link>
                  )}
                  <button onClick={handleLogout} className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-rose-500 hover:bg-rose-50 cursor-pointer">
                    <div className="w-4 h-4 flex items-center justify-center"><i className="ri-logout-box-line" /></div>
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              <Link to="/login" className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap ${scrolled || !isHome ? 'text-slate-600 hover:text-teal-600' : 'text-white/90 hover:text-white'}`}>
                Sign In
              </Link>
              <Link to="/signup" className="px-5 py-2 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap">
                Get Started
              </Link>
            </>
          )}
        </div>

        <button className={`md:hidden w-8 h-8 flex items-center justify-center rounded-lg cursor-pointer ${scrolled || !isHome ? 'text-slate-600' : 'text-white'}`} onClick={() => setMenuOpen(!menuOpen)}>
          <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`} />
        </button>
      </div>

      {menuOpen && (
        <div className="md:hidden bg-white border-t border-slate-100 px-4 py-3">
          {navLinks.map(link => (
            <Link key={link.to} to={link.to} className="block px-3 py-2.5 text-sm font-medium text-slate-600 hover:text-teal-600 rounded-lg cursor-pointer">{link.label}</Link>
          ))}
          {!isAuthenticated && (
            <div className="flex gap-2 mt-2 pt-2 border-t border-slate-100">
              <Link to="/login" className="flex-1 text-center px-4 py-2 text-sm font-medium text-slate-600 border border-slate-200 rounded-lg cursor-pointer">Sign In</Link>
              <Link to="/signup" className="flex-1 text-center px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg cursor-pointer">Get Started</Link>
            </div>
          )}
          {isAuthenticated && (
            <button onClick={handleLogout} className="w-full mt-2 pt-2 border-t border-slate-100 text-left px-3 py-2.5 text-sm font-medium text-rose-500 cursor-pointer">Sign Out</button>
          )}
        </div>
      )}
    </header>
  );
}
